import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Form, Loader, Modal, Typography, Iconography } from '@lucid/lego';
import { getGenerateDocumentsFormFields } from './formFields';
import type { SelectOption, BuyerOrderData } from './formFields';
import { useStore } from '../../../../../../../store/store';
import styles from './GenerateDocuments.module.scss';

export type GenerateDocumentsFormData = {
  deliveryInstructions: string;
  generalNotes: string;
  openDueBillStatus: string;
  serviceCenterLocation: string;
};

export type GenerateDocumentsProps = {
  errorMessage?: string | null;
  isOpen: boolean;
  isSubmitting?: boolean;
  labels: Record<string, string>;
  onClose: () => void;
  onSubmit: (data: GenerateDocumentsFormData) => void;
  openDueBillStatusOptions: SelectOption[];
  orderData?: BuyerOrderData;
  serviceCenterLocationOptions: SelectOption[];
  theme: 'light' | 'dark';
};

/**
 * Renders the documents generation modal for customer handoff.
 * Uses the Form component from lego for field management and validation.
 *
 * @component
 * @param props - Component props
 * @param props.isOpen - Controls modal visibility
 * @param props.labels - i18n labels map
 * @param props.onClose - Close handler for modal actions
 * @param props.onSubmit - Submit handler receiving validated form data
 * @param props.openDueBillStatusOptions - Due bill status dropdown options
 * @param props.serviceCenterLocationOptions - Service center dropdown options
 * @param props.theme - Theme used by modal controls
 * @returns Documents modal with buyer/payment info, notes form, and due bill fields
 * @example
 * <GenerateDocuments
 *   isOpen
 *   labels={{}}
 *   onClose={() => undefined}
 *   onSubmit={(data) => undefined}
 *   openDueBillStatusOptions={[]}
 *   serviceCenterLocationOptions={[]}
 *   theme="light"
 * />
 */
const GenerateDocuments: React.FC<GenerateDocumentsProps> = ({
  errorMessage,
  isOpen,
  isSubmitting,
  labels,
  onClose,
  onSubmit,
  openDueBillStatusOptions,
  orderData,
  serviceCenterLocationOptions,
  theme,
}) => {
  const locale = useStore((state) => state.locale);
  const formFields = useMemo(
    () =>
      getGenerateDocumentsFormFields(
        { onCancel: onClose, openDueBillStatusOptions, serviceCenterLocationOptions, locale },
        labels,
        orderData
      ),
    [labels, openDueBillStatusOptions, serviceCenterLocationOptions, onClose, orderData, locale]
  );

  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (errorMessage && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [errorMessage]);

  const handleSuccess = (data: unknown) => {
    const formData = data as GenerateDocumentsFormData;
    onSubmit(formData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={isSubmitting ? () => undefined : onClose}
      title={labels?.['generateDocuments.modalTitle'] ?? 'Generate Documents'}
      theme={theme}
      size="large"
      dataTestId="generate-documents-modal"
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <Box className={styles.modalContent}>
        {isSubmitting && (
          <div className="fetchingOverlay" data-testid="generate-documents-loader">
            <Loader theme={theme} />
          </div>
        )}
        {errorMessage && (
          <Box ref={errorRef} mb={16} data-testid="generate-documents-error">
            <Box className={styles.errorBanner}>
              <Iconography
                category="global"
                name="information"
                color="white"
                theme={theme}
                dataTestId="generate-documents-error-icon"
              />
              <Typography variant="body" size="medium" theme={theme} color="white">
                {errorMessage}
              </Typography>
            </Box>
          </Box>
        )}
        <Form
          id="generate-documents-form"
          formFields={formFields}
          theme={theme}
          onSuccess={handleSuccess}
          alignment="left"
          isFluid
          disabled={isSubmitting}
        />
      </Box>
    </Modal>
  );
};

GenerateDocuments.displayName = 'GenerateDocuments';

export default GenerateDocuments;
